import React, { useState } from 'react';
import authService from '../../services/authService';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout, Menu, Avatar, Dropdown,
  Badge, Tooltip, Typography,
} from 'antd';
import {
  DashboardOutlined,
  TeamOutlined,
  UserAddOutlined,
  UploadOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  BellOutlined,
  SettingOutlined,
  UserOutlined,
  SafetyCertificateOutlined,
  FallOutlined,
  PhoneOutlined,
  BarChartOutlined,
  ContactsOutlined,
  FileExcelOutlined,
  ToolOutlined,
  AppstoreOutlined,
  PieChartOutlined,
  FileTextOutlined,
  InboxOutlined,
  UsergroupAddOutlined,
  SolutionOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import './AppLayout.css';
import logo from '../../assets/logo/ErisConnectLogo.png';

const { Sider, Header, Content } = Layout;
const { Text } = Typography;

// ── Nav items — grouped with sub-menus ───────────────────────────
const navItems = [
  // ── Overview ─────────────────────────────────────────────────
  {
    key: '/dashboard',
    icon: <DashboardOutlined />,
    label: 'Dashboard',
  },

  // ── Analytics group ───────────────────────────────────────────
  {
    key: 'grp-analytics',
    icon: <BarChartOutlined />,
    label: 'Analytics',
    children: [
      { key: '/cohort',     icon: <PieChartOutlined />,  label: 'Cohort Analytics' },
      { key: '/attrition',  icon: <FallOutlined />,      label: 'Attrition' },
      { key: '/compliance', icon: <SafetyCertificateOutlined />, label: 'Compliance' },
    ],
  },

  // ── Employees group ───────────────────────────────────────────
  {
    key: 'grp-employees',
    icon: <ContactsOutlined />,
    label: 'Employees',
    children: [
      { key: '/employees',   icon: <TeamOutlined />,    label: 'Employee List' },
      { key: '/add',         icon: <UserAddOutlined />, label: 'Add Employee' },
      { key: '/bulk-upload', icon: <UploadOutlined />,  label: 'Bulk Upload' },
    ],
  },

  // ── Calls group ───────────────────────────────────────────────
  {
    key: 'grp-calls',
    icon: <PhoneOutlined />,
    label: 'Call Management',
    children: [
      { key: '/call-bulk-upload', icon: <FileExcelOutlined />, label: 'Call Bulk Upload' },
      { key: '/inbound',          icon: <InboxOutlined />,     label: 'Inbound Queries'  },
    ],
  },

  // ── Campus Hiring group ───────────────────────────────────────
  {
    key: 'grp-campus',
    icon: <SolutionOutlined />,
    label: 'Campus Hiring',
    children: [
      { key: '/campus', icon: <SolutionOutlined />, label: 'Campus Tracker' },
    ],
  },
  // ── Attrition & Occupancy ────────────────────────────────────
  {
    key: '/attrition-occupancy',
    icon: <LineChartOutlined />,
    label: 'Attrition & Occupancy',
  },

  // ── Configuration group ───────────────────────────────────────
  {
    key: 'grp-config',
    icon: <ToolOutlined />,
    label: 'Configuration',
    children: [
      {
        key: '/admin/cohort-config',
        icon: <AppstoreOutlined />,
        label: 'Cohort Config',
      },
      {
        key: '/admin/users',
        icon: <UsergroupAddOutlined />,
        label: 'Manage Users',
      },
      {
        key: '/admin/system-log',
        icon: <FileTextOutlined />,
        label: 'System Log',
      },
    ],
  },
];

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const user = authService.getUser();

  // Get initials for avatar
  const getInitials = (name = '') => {
    return name
      .split(' ')
      .map(n => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'A';
  };

  // Active menu key — match by pathname
  // Full pathname as active key — Menu matches exact keys
  const activeKey = location.pathname;

  // Logout
  const handleLogout = () => {
    localStorage.removeItem('ejt_token');
    localStorage.removeItem('ejt_user');
    navigate('/login');
  };

  // User dropdown menu
  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'My Profile',
      onClick: () => { },
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => { },
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
      onClick: handleLogout,
    },
  ];

  // Page title from current route
  const pageTitles = {
    '/dashboard':          'Dashboard',
    '/cohort':             'Cohort Analytics',
    '/attrition':          'Attrition Dashboard',
    '/attrition/upload':   'Attrition Upload',
    '/compliance':         'Compliance Records',
    '/employees':          'Employees',
    '/add':                'Add Employee',
    '/bulk-upload':        'Bulk Upload',
    '/call-bulk-upload':   'Call Bulk Upload',
    '/inbound':            'Inbound Query Management',
    '/campus':             'Campus Hiring Tracker',
    '/admin':              'Configuration',
    '/admin/system-log':   'System Log',
    '/admin/users':        'User Management',
    '/attrition-occupancy':'Attrition & Occupancy',
  };
  // For nested routes like /admin/cohort-config
  const pageTitle = pageTitles[activeKey]
    || pageTitles['/' + location.pathname.split('/')[1]]
    || 'ErisConnect';

  return (
    <Layout className="ejt-layout">

      {/* ── Sidebar ── */}
      <Sider
        theme="dark"
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        trigger={null}
        width={240}
        collapsedWidth={72}
        className="ejt-sider"
      >
        {/* Logo */}
<div className={`ejt-logo ${collapsed ? 'ejt-logo-collapsed' : ''}`}>
  {collapsed ? (
    <div className="ejt-logo-icon">
      <img
        src={logo}
        alt="Eris Connect"
        className="ejt-logo-img-small"
      />
    </div>
  ) : (
    <div className="ejt-logo-full">
      <img
        src={logo}
        alt="Eris Connect"
        className="ejt-logo-img-full"
      />
      <div className="ejt-logo-sub">Journey Tracker</div>
    </div>
  )}
</div>

        {/* Navigation */}
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[activeKey]}
          defaultOpenKeys={[]}
          items={navItems}
          onClick={({ key }) => { if (!key.startsWith('grp-')) navigate(key); }}
          className="ejt-nav-menu"
        />

        {/* Sidebar Footer */}
        {!collapsed && (
          <div className="ejt-sider-footer">
            <div className="ejt-sider-footer-inner">
              <Avatar
                size={32}
                style={{ background: '#1E6FBF', fontSize: 12, flexShrink: 0 }}
              >
                {getInitials(user?.fullName)}
              </Avatar>
              <div className="ejt-sider-user">
                <div className="ejt-sider-user-name">
                  {user?.fullName || 'Admin'}
                </div>
                <div className="ejt-sider-user-role">
                  {user?.role || 'admin'}
                </div>
              </div>
            </div>
          </div>
        )}
      </Sider>

      {/* ── Main area ── */}
      <Layout className="ejt-main">

        {/* Header */}
        <Header className="ejt-header">

          {/* Left — collapse toggle + page title */}
          <div className="ejt-header-left">
            <button
              className="ejt-collapse-btn"
              onClick={() => setCollapsed(c => !c)}
            >
              {collapsed
                ? <MenuUnfoldOutlined />
                : <MenuFoldOutlined />
              }
            </button>
            <div className="ejt-page-title">
              <Text className="ejt-page-title-text">{pageTitle}</Text>
              <Text className="ejt-page-title-sub">
                {new Date().toLocaleDateString('en-IN', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </Text>
            </div>
          </div>

          {/* Right — notifications + user */}
          <div className="ejt-header-right">

            {/* Notification bell */}
            <Tooltip title="Notifications">
              <Badge count={3} size="small" offset={[-2, 2]}>
                <button className="ejt-icon-btn">
                  <BellOutlined />
                </button>
              </Badge>
            </Tooltip>

            {/* Divider */}
            <div className="ejt-header-divider" />

            {/* User dropdown */}
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              trigger={['click']}
            >
              <div className="ejt-user-trigger">
                <div className="ejt-user-info">
                  <div className="ejt-user-name">
                    {user?.fullName || 'Admin'}
                  </div>
                  <div className="ejt-user-role">
                    {user?.role || 'admin'}
                  </div>
                </div>
                <Avatar
                  size={36}
                  style={{
                    background: '#1E6FBF',
                    fontSize: 13,
                    fontWeight: 600,
                    flexShrink: 0,
                    cursor: 'pointer',
                  }}
                >
                  {getInitials(user?.fullName)}
                </Avatar>
              </div>
            </Dropdown>

          </div>
        </Header>

        {/* Page content */}
        <Content className="ejt-content">
          <div className="ejt-content-inner">
            <Outlet />
          </div>
        </Content>

      </Layout>
    </Layout>
  );
}